import * as React from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import Chip, { ChipProps } from '@mui/material/Chip';

export interface ThemeTokens {
  bg: string; surface: string; surfaceAlt: string;
  ink: string; ink2: string; ink3: string; ink4: string;
  line: string; line2: string;
  primary: string; primary2: string; primarySoft: string;
  success: string; successSoft: string;
  warn: string; warnSoft: string;
  danger: string; dangerSoft: string;
  info: string; infoSoft: string;
  logic: string; logicSoft: string;
  calc: string; calcSoft: string;
  shadow1: string; shadow2: string; shadow3: string;
  font: string; fontMono: string;
  r1: number; r2: number; r3: number; r4: number;
}

export const NT: ThemeTokens = {
  bg:          '#f8f7fa',
  surface:     '#ffffff',
  surfaceAlt:  '#f5f5f8',
  ink:         '#262626',
  ink2:        '#3d3d3d',
  ink3:        '#8c8c8c',
  ink4:        '#a3a3a3',
  line:        '#e6ebf1',
  line2:       '#d6d9e0',
  primary:     '#3c5bfa',
  primary2:    '#3651e1',
  primarySoft: '#ebeefe',
  success:     '#168316',
  successSoft: '#e8f5e9',
  warn:        '#b8732b',
  warnSoft:    '#fff3e0',
  danger:      '#a80101',
  dangerSoft:  '#fce8e8',
  info:        '#035283',
  infoSoft:    '#e3f2fd',
  logic:       '#6d51a8',
  logicSoft:   '#ede7f6',
  calc:        '#1f7a7a',
  calcSoft:    '#e0f2f1',
  shadow1:     '0 1px 2px rgba(0,0,0,.06), 0 1px 3px rgba(0,0,0,.04)',
  shadow2:     '0 4px 14px rgba(0,0,0,.08), 0 1px 3px rgba(0,0,0,.06)',
  shadow3:     '0 12px 40px rgba(0,0,0,.14), 0 2px 8px rgba(0,0,0,.08)',
  font:        '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontMono:    '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
  r1: 6, r2: 8, r3: 12, r4: 16,
};

export type NMButtonVariant = 'contained' | 'soft' | 'text' | 'outlined';
export type NMButtonColor   = 'primary' | 'neutral' | 'success' | 'danger';
export type NMButtonSize    = 'sm' | 'md' | 'lg';

export interface NMButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  variant?: NMButtonVariant;
  color?: NMButtonColor;
  size?: NMButtonSize;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

const NM_MUI_COLOR: Record<NMButtonColor, ButtonProps['color']> = {
  primary: 'primary',
  neutral: 'inherit',
  success: 'success',
  danger:  'error',
};

export function NMButton({
  children, variant = 'contained', color = 'primary', size = 'md',
  icon, style, onClick, disabled, fullWidth, type, ...rest
}: NMButtonProps): JSX.Element {
  const muiVariant: ButtonProps['variant'] =
    variant === 'soft' ? 'contained'
    : variant === 'contained' ? 'contained'
    : variant === 'outlined' ? 'outlined'
    : 'text';

  const muiSize: ButtonProps['size'] = size === 'lg' ? 'large' : size === 'sm' ? 'small' : 'medium';
  const muiColor = NM_MUI_COLOR[color];

  const softSx = variant === 'soft' ? {
    backgroundColor: color === 'danger' ? NT.dangerSoft : color === 'success' ? NT.successSoft : NT.primarySoft,
    color: color === 'danger' ? NT.danger : color === 'success' ? NT.success : NT.primary2,
    '&:hover': {
      backgroundColor: color === 'danger' ? '#f5cccc' : color === 'success' ? '#c8e6c9' : '#d8defe',
    },
    boxShadow: 'none',
  } : {};

  return (
    <Button
      variant={muiVariant}
      color={muiColor}
      size={muiSize}
      startIcon={icon}
      onClick={onClick}
      disabled={disabled}
      fullWidth={fullWidth}
      type={(type as ButtonProps['type']) ?? 'button'}
      sx={{
        textTransform: 'none',
        fontFamily: NT.font,
        fontWeight: 600,
        letterSpacing: '0.01em',
        ...softSx,
        ...style,
      }}
      {...(rest as any)}
    >
      {children}
    </Button>
  );
}

export type NMChipColor =
  | 'neutral' | 'primary' | 'success' | 'warn' | 'danger' | 'info' | 'logic' | 'calc';

const CHIP_CUSTOM_COLORS: Partial<Record<NMChipColor, { bg: string; fg: string }>> = {
  warn:  { bg: NT.warnSoft,  fg: NT.warn  },
  logic: { bg: NT.logicSoft, fg: NT.logic },
  calc:  { bg: NT.calcSoft,  fg: NT.calc  },
};

const NM_MUI_CHIP_COLOR: Record<NMChipColor, ChipProps['color']> = {
  neutral: 'default',
  primary: 'primary',
  success: 'success',
  warn:    'warning',
  danger:  'error',
  info:    'info',
  logic:   'default',
  calc:    'default',
};

export interface NMChipProps {
  children?: React.ReactNode;
  color?: NMChipColor;
  style?: React.CSSProperties;
  icon?: React.ReactNode;
}

export function NMChip({ children, color = 'neutral', style, icon }: NMChipProps): JSX.Element {
  const custom = CHIP_CUSTOM_COLORS[color];
  return (
    <Chip
      label={children}
      color={NM_MUI_CHIP_COLOR[color]}
      size="small"
      icon={icon ? <span style={{ display: 'inline-flex', fontSize: 12 }}>{icon}</span> : undefined}
      sx={{
        fontFamily: NT.font,
        fontWeight: 600,
        fontSize: '11px',
        height: 22,
        ...(custom ? { backgroundColor: custom.bg, color: custom.fg } : {}),
        ...style,
      }}
    />
  );
}

export interface ChevProps { d?: 'up' | 'down' | 'left' | 'right'; }

export interface IconLibrary {
  Plus: React.FC; Check: React.FC; Close: React.FC; Chev: React.FC<ChevProps>;
  Drag: React.FC; Trash: React.FC; Copy: React.FC; Eye: React.FC;
  Branch: React.FC; Calc: React.FC; Repeat: React.FC; Search: React.FC;
  Edit: React.FC; Settings: React.FC; History: React.FC; Sparkle: React.FC;
  Warn: React.FC; Link: React.FC; Book: React.FC; Text: React.FC;
  Number: React.FC; Radio: React.FC; Check2: React.FC; Toggle: React.FC;
  Group: React.FC; Money: React.FC; Percent: React.FC; Ruler: React.FC;
  Cal: React.FC; Clock: React.FC; Fork: React.FC; Validate: React.FC;
  ArrowL: React.FC; Lock: React.FC; Save: React.FC; Rocket: React.FC;
  Density: React.FC; Camera: React.FC;
}

export const NI: IconLibrary = {
  Plus:    () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M8 3v10M3 8h10"/></svg>,
  Check:   () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8.5l3 3 7-7.5"/></svg>,
  Close:   () => <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 4l8 8M12 4l-8 8"/></svg>,
  Chev:    ({ d = 'down' }: ChevProps) => {
    const path = d === 'down' ? 'M3 6l5 5 5-5' : d === 'up' ? 'M3 10l5-5 5 5' : d === 'right' ? 'M6 3l5 5-5 5' : 'M10 3L5 8l5 5';
    return <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={path}/></svg>;
  },
  Drag:    () => <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor"><circle cx="2" cy="2.5" r="1.1"/><circle cx="8" cy="2.5" r="1.1"/><circle cx="2" cy="7" r="1.1"/><circle cx="8" cy="7" r="1.1"/><circle cx="2" cy="11.5" r="1.1"/><circle cx="8" cy="11.5" r="1.1"/></svg>,
  Trash:   () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4h10M6.5 4V2.5h3V4M5 4l.5 9a1 1 0 001 1h3a1 1 0 001-1L11 4"/></svg>,
  Copy:    () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="8" height="9" rx="1.5"/><path d="M5 5V3a1 1 0 011-1h6a1 1 0 011 1v8a1 1 0 01-1 1h-2"/></svg>,
  Eye:     () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z"/><circle cx="8" cy="8" r="2"/></svg>,
  Branch:  () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="4" cy="3" r="1.5"/><circle cx="4" cy="13" r="1.5"/><circle cx="12" cy="8" r="1.5"/><path d="M4 4.5v7M4 8h4.5"/></svg>,
  Calc:    () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="2" width="10" height="12" rx="1.5"/><path d="M5 5h6M5.5 8h.01M8 8h.01M10.5 8h.01M5.5 10.5h.01M8 10.5h.01M10.5 10.5h5"/></svg>,
  Repeat:  () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6l2-2 2 2M5 4v6a2 2 0 002 2h6M13 10l-2 2-2-2M11 12V6a2 2 0 00-2-2H3"/></svg>,
  Search:  () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="7" cy="7" r="4.5"/><path d="M10.5 10.5L14 14"/></svg>,
  Edit:    () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M11 2.5l2.5 2.5L6 12.5l-3 .5.5-3z"/></svg>,
  Settings:() => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="2"/><path d="M8 1.5v2M8 12.5v2M14.5 8h-2M3.5 8h-2M12.6 3.4l-1.4 1.4M4.8 11.2l-1.4 1.4M12.6 12.6l-1.4-1.4M4.8 4.8L3.4 3.4"/></svg>,
  History: () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 8a6 6 0 108-5.7M2 2.5V6h3.5M8 4v4l2.5 1.5"/></svg>,
  Sparkle: () => <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1l1.3 3.7L13 6l-3.7 1.3L8 11l-1.3-3.7L3 6l3.7-1.3zM13 10l.6 1.6L15 12l-1.4.4L13 14l-.6-1.6L11 12l1.4-.4z"/></svg>,
  Warn:    () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2l6.5 11H1.5L8 2z"/><path d="M8 6v3.5M8 11.5h.01"/></svg>,
  Link:    () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M7 9.5a3 3 0 004.2 0l2-2a3 3 0 00-4.2-4.2l-1 1"/><path d="M9 6.5a3 3 0 00-4.2 0l-2 2a3 3 0 004.2 4.2l1-1"/></svg>,
  Book:    () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 3.5A1.5 1.5 0 014 2h9v11H4a1.5 1.5 0 00-1.5 1.5v-11zM2.5 3.5v11"/></svg>,
  Text:    () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3 4h10M8 4v9M5.5 13h5"/></svg>,
  Number:  () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M6 2L4.5 14M11.5 2L10 14M2.5 6h11M2 10h11"/></svg>,
  Radio:   () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="8" cy="8" r="5.5"/><circle cx="8" cy="8" r="2.5" fill="currentColor"/></svg>,
  Check2:  () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2.5" y="2.5" width="11" height="11" rx="2"/><path d="M5 8l2 2 4-4"/></svg>,
  Toggle:  () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1.5" y="4.5" width="13" height="7" rx="3.5"/><circle cx="11" cy="8" r="2" fill="currentColor"/></svg>,
  Group:   () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2" y="2" width="12" height="12" rx="1.5"/><path d="M2 6h12"/></svg>,
  Money:   () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M8 2v12M11 4.5H6.5a2 2 0 100 4H9a2 2 0 110 4H4.5"/></svg>,
  Percent: () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3 13L13 3"/><circle cx="4.5" cy="4.5" r="2"/><circle cx="11.5" cy="11.5" r="2"/></svg>,
  Ruler:   () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2 10l4-8 8 4-4 8z"/><path d="M5 5l1 .5M4 7l2 1M3 9l2 1M9 8l1 .5M7 10l2 1"/></svg>,
  Cal:     () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="12" height="11" rx="1.5"/><path d="M2 6.5h12M5.5 2v2.5M10.5 2v2.5"/></svg>,
  Clock:   () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6"/><path d="M8 4.5V8l2.5 1.5"/></svg>,
  Fork:    () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="4" cy="3.5" r="1.5"/><circle cx="12" cy="3.5" r="1.5"/><circle cx="8" cy="13" r="1.5"/><path d="M4 5v3a2 2 0 002 2h4a2 2 0 002-2V5M8 10v1.5"/></svg>,
  Validate:() => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M8 1.5l5 2v4.5c0 3-2.2 5.6-5 6.5-2.8-.9-5-3.5-5-6.5V3.5l5-2z"/><path d="M5.5 8l2 2 3.5-3.5"/></svg>,
  ArrowL:  () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4l-5 4 5 4M6 8h7"/></svg>,
  Lock:    () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="10" height="7" rx="1.5"/><path d="M5.5 7V5a2.5 2.5 0 015 0v2"/></svg>,
  Save:    () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 2.5h8L13.5 5.5v8a1 1 0 01-1 1h-9a1 1 0 01-1-1v-10a1 1 0 011-1z"/><path d="M4.5 2.5v4h6v-4M4.5 14v-5h7v5"/></svg>,
  Rocket:  () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2c2 0 4 2 4 4.5L9 10.5l-2-2L11 4.5c0-1.5-1-2.5-2-2.5z"/><path d="M7 8.5L4.5 11l1 1.5 2.5-1M9 10.5l-1.5 2.5 1.5 1 1-2.5"/></svg>,
  Density: () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h12M2 6.5h12M2 10h12M2 13.5h12"/></svg>,
  Camera:  () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="1.5" y="4" width="13" height="9.5" rx="1.5"/><circle cx="8" cy="9" r="2.5"/><path d="M5.5 4l1-1.5h3l1 1.5"/></svg>,
};

Object.assign(window, { NT, NI, NMButton, NMChip });
